import { type ReactElement, type ReactNode } from 'react';
import {
  Focusable,
  type MenuItemRenderProps,
  MenuItem as ReactAriaMenuItem,
  type MenuItemProps as ReactAriaMenuItemProps,
} from 'react-aria-components';
import cx from 'clsx';
import { Tooltip, type TooltipProps } from '../tooltip';
import styles from './menu-item.module.css';

interface MenuItemProps extends ReactAriaMenuItemProps {
  beforeContent?: ReactNode | ((values: MenuItemRenderProps) => ReactNode);
  afterContent?: ReactNode | ((values: MenuItemRenderProps) => ReactNode);
  afterContentClassName?: string;
  beforeContentClassName?: string;
  contentClassName?: string;
  tooltip?: Pick<TooltipProps, 'label' | 'placement' | 'isDisabled' | 'tooltipTestId'>;
}

export function MenuItem({
  beforeContent,
  afterContent,
  afterContentClassName,
  beforeContentClassName,
  contentClassName,
  tooltip,
  className,
  children,
  ...props
}: MenuItemProps): ReactElement {
  return (
    <ReactAriaMenuItem className={styles.menuItem} {...props}>
      {menuItemRenderProps => (
        <Tooltip isDisabled={!tooltip} label="" {...tooltip}>
          <Focusable>
            <div className={cx(styles.container, className)}>
              {beforeContent ? (
                <div className={cx(styles.beforeContent, beforeContentClassName)}>
                  {typeof beforeContent === 'function'
                    ? beforeContent(menuItemRenderProps)
                    : beforeContent}
                </div>
              ) : null}
              <span className={cx(styles.content, contentClassName)}>
                {typeof children === 'function' ? children(menuItemRenderProps) : children}
              </span>
              {afterContent ? (
                <div className={cx(styles.afterContent, afterContentClassName)}>
                  {typeof afterContent === 'function'
                    ? afterContent(menuItemRenderProps)
                    : afterContent}
                </div>
              ) : null}
            </div>
          </Focusable>
        </Tooltip>
      )}
    </ReactAriaMenuItem>
  );
}
